import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../contexts/AppContext';
import { fmtDatum, tijdNaarMinuten, berekenUrenPeriode, berekenVerlofUrenHeleDag } from '../utils/datum';
import sharepointConfig from '../config/sharepointConfig';
import Verlofwijzer from './Verlofwijzer';

function LeaveRequest() {
  const navigate = useNavigate();
  const { currentUser, isAdmin, isManager, canManage, saldo, medewerkers, verlofTypes, indienVerlofaanvraag, feestdagen, vakantiePerioden } = useApp();
  const [selectedMedewerkerId, setSelectedMedewerkerId] = useState('');
  const [form, setForm] = useState({
    Type: '', StartDatum: '', EindDatum: '', Uren: '', Reden: '',
    isDagdeel: false, StartTijd: '', EindTijd: '',
    gebruikOveruren: false, directGoedkeuren: true,
    bijzonderCategorie: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [toonVerlofwijzer, setToonVerlofwijzer] = useState(false);
  const [wijzerResultaat, setWijzerResultaat] = useState(null);

  // Derived state — ALL hooks BEFORE any conditional returns
  const beschikbareMedewerkers = useMemo(() =>
    (medewerkers || []).filter(m => (m.Status || 'actief') === 'actief' && m.Rol !== 'flexkracht'),
    [medewerkers]
  );
  const isVoorAnder = isManager && selectedMedewerkerId && selectedMedewerkerId !== currentUser?.id;
  const targetUser = isVoorAnder ? medewerkers.find(m => m.Id === selectedMedewerkerId) : null;
  const effectiefUser = targetUser || currentUser;
  const werkrooster = effectiefUser?.Werkrooster || null;

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setError('');
    setFieldErrors(prev => { const n = { ...prev }; delete n[field]; return n; });
    // Verlof medische (kort-verzuim): verlofwijzer altijd automatisch openen
    if (field === 'Type' && value === 'kort-verzuim') {
      setWijzerResultaat(null);
      setToonVerlofwijzer(true);
    } else if (field === 'Type' && value !== 'kort-verzuim') {
      setToonVerlofwijzer(false);
    }
  };

  const feestdagenList = useMemo(() => (feestdagen || []).map(f => f.Datum), [feestdagen]);

  const periodeInfo = useMemo(() => {
    try {
      if (!form.isDagdeel || !form.StartDatum) return null;
      const ed = form.EindDatum || form.StartDatum;
      return berekenUrenPeriode(form.StartDatum, form.StartTijd || null, ed, form.EindTijd || null, feestdagenList, werkrooster);
    } catch (err) { console.error('periodeInfo berekening mislukt:', err); return null; }
  }, [form.isDagdeel, form.StartDatum, form.EindDatum, form.StartTijd, form.EindTijd, feestdagenList, werkrooster]);

  const heleDagInfo = useMemo(() => {
    try {
      if (form.isDagdeel || !form.StartDatum || !form.EindDatum) return null;
      return berekenVerlofUrenHeleDag(form.StartDatum, form.EindDatum, feestdagenList, werkrooster);
    } catch (err) { console.error('heleDagInfo berekening mislukt:', err); return null; }
  }, [form.isDagdeel, form.StartDatum, form.EindDatum, feestdagenList, werkrooster]);

  useEffect(() => {
    if (form.isDagdeel) {
      if (periodeInfo && periodeInfo.netto > 0) setForm(prev => ({ ...prev, Uren: String(periodeInfo.netto) }));
      else setForm(prev => ({ ...prev, Uren: '' }));
    } else {
      if (heleDagInfo && heleDagInfo.uren > 0) setForm(prev => ({ ...prev, Uren: String(heleDagInfo.uren) }));
      else setForm(prev => ({ ...prev, Uren: '' }));
    }
  }, [form.isDagdeel, periodeInfo, heleDagInfo]);

  const uren = parseFloat(String(form.Uren).replace(',', '.')) || 0;
  const isFlexType = form.Type === 'flex' || form.Type === 'flex-plus';

  const beschikbareTypes = useMemo(() =>
    (verlofTypes || []).filter(type => type.key !== 'ziek' && (
      (type.key !== 'flex' && type.key !== 'flex-plus') || (targetUser || currentUser)?.FlexEnabled
    )), [verlofTypes, targetUser, currentUser]
  );

  // === CONDITIONAL RETURNS — only AFTER all hooks ===
  if (isAdmin) {
    return (
      <div style={styles.container}>
        <div style={styles.formCard} className="r-card">
          <h2 style={styles.formTitle}>📝 Indienen & Registreren</h2>
          <p style={{ fontSize: 14, color: '#605e5c', marginBottom: 24 }}>
            Kies wat je wilt doen. Of gebruik het snelformulier op het Dashboard.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <button style={styles.hubBtn} onClick={() => navigate('/')}>
              <span style={styles.hubIcon}>📋</span>
              <div>
                <div style={styles.hubLabel}>Verlof indienen</div>
                <div style={styles.hubDesc}>Via het snelformulier op het Dashboard</div>
              </div>
              <span style={styles.hubArrow}>→</span>
            </button>
            <button style={styles.hubBtn} onClick={() => navigate('/ziekmelding')}>
              <span style={styles.hubIcon}>🏥</span>
              <div>
                <div style={styles.hubLabel}>Ziekmelding</div>
                <div style={styles.hubDesc}>Ziek- of betermelding registreren</div>
              </div>
              <span style={styles.hubArrow}>→</span>
            </button>
            <button style={styles.hubBtn} onClick={() => navigate('/overuren')}>
              <span style={styles.hubIcon}>⏰</span>
              <div>
                <div style={styles.hubLabel}>Overuren registreren</div>
                <div style={styles.hubDesc}>Overuren toevoegen, goedkeuren of uitbetalen</div>
              </div>
              <span style={styles.hubArrow}>→</span>
            </button>
            {(currentUser?.FlexEnabled || medewerkers?.some(m => m.FlexEnabled)) && (
              <button style={styles.hubBtn} onClick={() => navigate('/flex')}>
                <span style={styles.hubIcon}>⚡</span>
                <div>
                  <div style={styles.hubLabel}>Flex/ADV planning</div>
                  <div style={styles.hubDesc}>Flexkracht uren inplannen en beheren</div>
                </div>
                <span style={styles.hubArrow}>→</span>
              </button>
            )}
            <button style={styles.hubBtn} onClick={() => navigate('/admin', { state: { tab: 'uitbetalen' } })}>
              <span style={styles.hubIcon}>💰</span>
              <div>
                <div style={styles.hubLabel}>Uitbetalen overuren / verlofuren</div>
                <div style={styles.hubDesc}>Flex uitbetalingen en overuren markeren als uitbetaald</div>
              </div>
              <span style={styles.hubArrow}>→</span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  const handleSubmit = async () => {
    const errors = {};
    if (isManager && !selectedMedewerkerId) errors.medewerker = 'Kies een medewerker';
    if (!form.Type) errors.Type = 'Kies een verloftype';
    if (!form.StartDatum) errors.StartDatum = 'Vul een startdatum in';

    if (form.isDagdeel) {
      if (form.EindDatum && new Date(form.EindDatum) < new Date(form.StartDatum)) errors.EindDatum = 'Einddatum kan niet voor startdatum liggen';
      if (form.StartDatum && (!form.EindDatum || form.EindDatum === form.StartDatum)) {
        if (form.StartTijd && !form.EindTijd) errors.EindTijd = 'Vul een eindtijd in (zelfde dag)';
        if (!form.StartTijd && form.EindTijd) errors.StartTijd = 'Vul een starttijd in (zelfde dag)';
      }
      if (form.StartTijd && form.EindTijd && form.StartDatum === (form.EindDatum || form.StartDatum) && tijdNaarMinuten(form.EindTijd) <= tijdNaarMinuten(form.StartTijd)) {
        errors.EindTijd = 'Eindtijd moet na starttijd liggen';
      }
      if (uren <= 0 && !errors.StartDatum) errors.Uren = 'Uren berekening is 0 — controleer datums, tijden en werkrooster';
    } else {
      if (!form.EindDatum) errors.EindDatum = 'Vul een einddatum in';
      if (form.EindDatum && new Date(form.EindDatum) < new Date(form.StartDatum)) errors.EindDatum = 'Einddatum kan niet voor startdatum liggen';
      if (uren <= 0 && form.StartDatum && form.EindDatum) errors.Uren = 'Geen werkdagen gevonden — controleer datums en werkrooster';
    }

    if (Object.keys(errors).length > 0) { setFieldErrors(errors); setError('Vul de gemarkeerde velden in.'); return; }
    // Bijzonder verlof: categorie verplicht
    if (form.Type === 'bijzonder' && !form.bijzonderCategorie) { setFieldErrors({ bijzonderCategorie: 'Kies een categorie' }); setError('Kies een categorie voor bijzonder verlof.'); return; }
    if (form.Type === 'bijzonder' && form.bijzonderCategorie === 'overige' && !form.Reden.trim()) { setFieldErrors({ Reden: 'Bij "Overige" is een toelichting verplicht' }); setError('Vul een toelichting in bij bijzonder verlof "Overige".'); return; }
    // Verlof medische: Verlofwijzer altijd verplicht
    if (form.Type === 'kort-verzuim' && !wijzerResultaat) {
      setFieldErrors({ Type: 'Verlofwijzer verplicht bij Verlof medische' });
      setError('Bij Verlof medische moet je eerst de Verlofwijzer doorlopen.');
      setToonVerlofwijzer(true);
      return;
    }
    // Onbetaald verlof: Verlofwijzer verplicht + Reden verplicht
    if (form.Type === 'onbetaald' && !wijzerResultaat) { setFieldErrors({ Type: 'Gebruik eerst de Verlofwijzer' }); setError('Bij onbetaald verlof moet je eerst de Verlofwijzer invullen.'); setToonVerlofwijzer(true); return; }
    if (form.Type === 'onbetaald' && !form.Reden?.trim()) { setFieldErrors({ Reden: 'Opmerking is verplicht bij onbetaald verlof' }); setError('Vul een opmerking in bij onbetaald verlof.'); return; }
    setFieldErrors({});

    if (saldo && isFlexType) {
      if (form.Type === 'flex') {
        if ((saldo.flexMinDezeWeek + uren) > saldo.flexMinWeekMax) { setError(`Max ${saldo.flexMinWeekMax}u flex-min per week. Deze week al ${saldo.flexMinDezeWeek}u.`); return; }
        if (saldo.weekFlexUren > 0 && (saldo.flexMinDezeMaand + uren) > saldo.flexMinMaandMax) { setError(`Max ${saldo.flexMinMaandMax}u flex-min per maand.`); return; }
      }
      if (form.Type === 'flex-plus') {
        const maandMax = (saldo.weekFlexUren || 4) * 5;
        if (saldo.flexPlusDezeMaand + uren > maandMax) { setError(`Max ${maandMax}u flex-plus per maand.`); return; }
      }
    }

    try {
      setSubmitting(true);
      const selectedType = verlofTypes.find(t => t.key === form.Type);
      const isFlex = selectedType && selectedType.aftrekSaldo === 'flex';
      const canUseOveruren = !isFlex && form.gebruikOveruren && saldo?.overurenSaldo > 0;
      const overurenGebruikt = canUseOveruren ? Math.min(saldo.overurenSaldo, uren) : 0;

      const submitData = {
        Type: form.Type, StartDatum: form.StartDatum,
        EindDatum: form.isDagdeel ? (form.EindDatum || form.StartDatum) : form.EindDatum,
        Uren: uren, Reden: form.Reden || '', gebruikOveruren: canUseOveruren, overurenGebruikt,
      };
      // Bijzonder verlof categorie
      if (form.Type === 'bijzonder' && form.bijzonderCategorie) {
        submitData.bijzonderCategorie = form.bijzonderCategorie;
      }
      // Voeg verlofwijzer resultaat toe als referentie
      if (wijzerResultaat) {
        submitData.wijzerResultaat = wijzerResultaat.titel;
        submitData.wijzerWettelijk = wijzerResultaat.wettelijk || '';
      }
      if (form.isDagdeel) { submitData.StartTijd = form.StartTijd || null; submitData.EindTijd = form.EindTijd || null; submitData.isDagdeel = true; }
      if (isVoorAnder && targetUser) {
        submitData.medewerkerId = targetUser.Id;
        submitData.medewerkerNaam = targetUser.Naam;
        submitData.directGoedkeuren = form.directGoedkeuren;
      }
      await indienVerlofaanvraag(submitData);
      setSuccess(true);
      setTimeout(() => navigate(isManager ? '/' : '/verlof-overzicht'), 3000);
    } catch (err) { setError('Er ging iets mis: ' + err.message); } finally { setSubmitting(false); }
  };

  if (success) {
    return (
      <div style={styles.successCard}>
        <div style={styles.successIcon}>✅</div>
        <h2 style={styles.successTitle}>Aanvraag {isVoorAnder && form.directGoedkeuren ? 'goedgekeurd' : 'ingediend'}!</h2>
        <p style={styles.successText}>
          {`Verlofaanvraag voor ${uren} uur ${form.isDagdeel && (form.StartTijd || form.EindTijd) ? `(${form.StartTijd || 'hele dag'}–${form.EindTijd || 'hele dag'})` : ''} ${isVoorAnder ? `(voor ${targetUser?.Naam})` : ''} is succesvol ${isVoorAnder && form.directGoedkeuren ? 'goedgekeurd' : 'ingediend'}.`}
        </p>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.formCard} className="r-card">
        <h2 style={styles.formTitle}>Nieuwe verlofaanvraag</h2>

        {isManager && (
          <div style={styles.field}>
            <label style={styles.label}>Verlof aanvragen voor *</label>
            <select style={{ ...styles.select, ...(fieldErrors.medewerker ? styles.inputError : {}), ...(selectedMedewerkerId ? {} : { color: '#a19f9d' }) }} value={selectedMedewerkerId}
              onChange={(e) => { setSelectedMedewerkerId(e.target.value); setFieldErrors(prev => { const n = {...prev}; delete n.medewerker; return n; }); }}>
              <option value="" disabled>--- Kies ---</option>
              {beschikbareMedewerkers.map(m => (
                <option key={m.Id} value={m.Id}>{m.Naam} ({m.Afdeling})</option>
              ))}
            </select>
            {fieldErrors.medewerker && <div style={styles.fieldError}>⚠ {fieldErrors.medewerker}</div>}
            {isVoorAnder && targetUser && (
              <div style={styles.hintText}>
                {targetUser.Naam} heeft nog {(targetUser.BerekendeVerlofUren + targetUser.MeegenomenUren) - targetUser.VerlofOpgenomen} uren beschikbaar
              </div>
            )}
          </div>
        )}

        {(!isManager || selectedMedewerkerId) && (
          <>
            <p style={styles.formSubtitle}>
              {isVoorAnder ? `Aanvraag voor ${targetUser?.Naam}` : `Je hebt nog ${saldo?.resterend || 0} verlofuren beschikbaar.`}
            </p>

            <div style={styles.field}>
              <label style={styles.label}>Type verlof *</label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {beschikbareTypes.map((type) => {
                  const isSelected = form.Type === type.key;
                  return (
                    <button key={type.key} type="button" onClick={() => {
                      handleChange('Type', type.key);
                      if (type.key === 'flex' || type.key === 'flex-plus') handleChange('isDagdeel', true);
                      setWijzerResultaat(null);
                    }} style={{
                      display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 14px',
                      borderRadius: 10, border: `2px solid ${isSelected ? type.color : '#edebe9'}`,
                      background: isSelected ? `${type.color}08` : '#fff',
                      cursor: 'pointer', textAlign: 'left', transition: 'all 0.15s',
                    }}>
                      <span style={{
                        fontSize: 10, fontWeight: 800, color: isSelected ? '#fff' : type.color,
                        background: isSelected ? type.color : `${type.color}15`,
                        borderRadius: 4, padding: '2px 6px', lineHeight: 1.4, flexShrink: 0, marginTop: 1,
                      }}>{type.label}</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: isSelected ? type.color : '#323130' }}>{type.text}</div>
                        {type.beschrijving && <div style={{ fontSize: 11, color: '#605e5c', marginTop: 1 }}>{type.beschrijving}</div>}
                        <div style={{ fontSize: 10, color: type.aftrekSaldo === 'verlof' || type.aftrekSaldo === 'flex' ? '#E86E1C' : '#107C10', fontWeight: 600, marginTop: 2 }}>
                          {type.aftrekInfo || (type.aftrekSaldo === 'verlof' ? '✂️ Verlofuren aftrek' : type.aftrekSaldo === 'flex' ? '✂️ Flex aftrek' : '✅ Geen aftrek')}
                        </div>
                      </div>
                      {isSelected && <span style={{ fontSize: 16, flexShrink: 0 }}>✓</span>}
                    </button>
                  );
                })}
                {form.Type === 'kort-verzuim' && !wijzerResultaat && !toonVerlofwijzer && (
                  <div style={{ padding: '8px 14px', background: '#f0fff0', borderRadius: 8, border: '1px solid #107C10', fontSize: 11, color: '#107C10', marginTop: 4 }}>
                    🩺 <strong>Verlofwijzer wordt geopend</strong> — bepaalt of dit Verlof medische (geen aftrek) of verlofuren wordt
                  </div>
                )}
              </div>
              {fieldErrors.Type && <div style={styles.fieldError}>⚠ {fieldErrors.Type}</div>}

              {/* Bijzonder verlof — categorie verplicht */}
              {form.Type === 'bijzonder' && (() => {
                const bvType = sharepointConfig.verlofTypes.find(t => t.key === 'bijzonder');
                const opties = [
                  ...(bvType?.wettelijkMaximum || []).map(item => ({ key: item.gebeurtenis.toLowerCase().replace(/[^a-z]/g, '_').slice(0, 30), label: item.gebeurtenis, dagen: item.dagen })),
                  { key: 'overige', label: 'Overige (toelichting verplicht)', dagen: '' },
                ];
                return (
                  <div style={{ marginTop: 8, padding: '12px 14px', background: '#faf5ff', border: '1px solid #8764B8', borderRadius: 8 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#8764B8', marginBottom: 8 }}>📜 Reden bijzonder verlof *</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      {opties.map((opt) => {
                        const isSelected = form.bijzonderCategorie === opt.key;
                        return (
                          <button key={opt.key} type="button" onClick={() => setForm(f => ({ ...f, bijzonderCategorie: opt.key }))}
                            style={{
                              display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8,
                              padding: '8px 12px', borderRadius: 8, textAlign: 'left', cursor: 'pointer',
                              border: `1.5px solid ${isSelected ? '#8764B8' : '#edebe9'}`,
                              background: isSelected ? '#8764B810' : '#fff', transition: 'all 0.15s',
                            }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 1, minWidth: 0 }}>
                              {isSelected && <span style={{ color: '#8764B8', fontWeight: 700 }}>✓</span>}
                              <span style={{ fontSize: 12, fontWeight: isSelected ? 700 : 500, color: isSelected ? '#8764B8' : '#323130' }}>{opt.label}</span>
                            </div>
                            {opt.dagen && <span style={{ fontSize: 11, fontWeight: 600, color: '#605e5c', flexShrink: 0 }}>{opt.dagen}</span>}
                          </button>
                        );
                      })}
                    </div>
                    {fieldErrors.bijzonderCategorie && <div style={{ ...styles.fieldError, marginTop: 6 }}>⚠ {fieldErrors.bijzonderCategorie}</div>}
                    {form.bijzonderCategorie === 'overige' && (
                      <div style={{ marginTop: 8, fontSize: 11, color: '#D13438', fontWeight: 600 }}>
                        ⚠ Bij "Overige" is het opmerkingenveld hieronder verplicht
                      </div>
                    )}
                    <div style={{ fontSize: 10, color: '#a19f9d', marginTop: 8, fontStyle: 'italic' }}>
                      Bron: Retail/Wonen cao. Geen aftrek verlofuren tot wettelijk maximum.
                    </div>
                  </div>
                );
              })()}

              {/* Onbetaald verlof — opmerking verplicht banner */}
              {form.Type === 'onbetaald' && (
                <div style={{ marginTop: 8, padding: '10px 14px', background: '#fff8f0', border: '1px solid #E86E1C', borderRadius: 8, fontSize: 12 }}>
                  <strong style={{ color: '#E86E1C' }}>⚠ Opmerking verplicht bij onbetaald verlof</strong>
                  <div style={{ color: '#605e5c', marginTop: 4 }}>Dit is onbetaald verlof — geen loondoorbetaling. Vul het opmerkingenveld in met de reden.</div>
                </div>
              )}

              {/* Verlofwijzer trigger — niet bij Verlof medische (opent automatisch) */}
              {!toonVerlofwijzer && form.Type !== 'kort-verzuim' && (
                <button onClick={() => { setToonVerlofwijzer(true); setWijzerResultaat(null); }}
                  style={styles.wijzerTrigger}>
                  ⚖️ Twijfel? Gebruik de <strong>Verlofwijzer</strong> — bepaalt automatisch of er uren worden afgetrokken
                </button>
              )}

              {/* Verlofwijzer resultaat badge */}
              {wijzerResultaat && !toonVerlofwijzer && (
                <div style={{ ...styles.wijzerBadge, borderColor: wijzerResultaat.kleur, background: wijzerResultaat.aftrek ? '#fff8f0' : '#f0fff0' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: 13, fontWeight: 700, color: wijzerResultaat.kleur }}>
                      {wijzerResultaat.aftrek ? '📊' : '✅'} Verlofwijzer: {wijzerResultaat.titel}
                    </span>
                    <button onClick={() => { setToonVerlofwijzer(true); setWijzerResultaat(null); }}
                      style={{ background: 'none', border: 'none', color: '#8764B8', fontSize: 11, cursor: 'pointer', fontWeight: 600 }}>
                      🔄 Opnieuw
                    </button>
                  </div>
                  <div style={{ fontSize: 11, color: '#605e5c', marginTop: 4 }}>{wijzerResultaat.uitleg}</div>
                  {wijzerResultaat.wettelijk && (
                    <div style={{ fontSize: 10, color: '#a19f9d', marginTop: 4, fontStyle: 'italic' }}>📜 {wijzerResultaat.wettelijk}</div>
                  )}
                </div>
              )}

              {/* Verlofwijzer component */}
              {toonVerlofwijzer && (
                <Verlofwijzer
                  onResultaat={(res) => {
                    setWijzerResultaat(res);
                    // Verlofwijzer geeft 'verlof' → type verlof (snipperdag/vrije dag)
                    // 'vakantie' → type vakantie, 'kort-verzuim' → kort-verzuim, rest direct
                    setForm(prev => ({ ...prev, Type: res.type }));
                  }}
                  onSluit={() => setToonVerlofwijzer(false)}
                />
              )}
            </div>

            {form.Type && (
            <>
            <div style={styles.toggleRow}>
              <button style={{ ...styles.toggleBtn, ...(!form.isDagdeel ? styles.toggleBtnActive : {}) }}
                onClick={() => handleChange('isDagdeel', false)}>📅 Hele dag(en)</button>
              <button style={{ ...styles.toggleBtn, ...(form.isDagdeel ? styles.toggleBtnActive : {}) }}
                onClick={() => handleChange('isDagdeel', true)}>🕐 Met begin/eindtijd</button>
            </div>

            {!form.isDagdeel ? (
              <>
                <div style={styles.dateRow} className="r-form-row">
                  <div style={styles.field}>
                    <label style={styles.label}>Startdatum *</label>
                    <input type="date" style={{ ...styles.input, ...(fieldErrors.StartDatum ? styles.inputError : {}) }} value={form.StartDatum}
                      onChange={(e) => handleChange('StartDatum', e.target.value)} min={new Date().toISOString().split('T')[0]} />
                    {fieldErrors.StartDatum && <div style={styles.fieldError}>⚠ {fieldErrors.StartDatum}</div>}
                  </div>
                  <div style={styles.field}>
                    <label style={styles.label}>Einddatum *</label>
                    <input type="date" style={{ ...styles.input, ...(fieldErrors.EindDatum ? styles.inputError : {}) }} value={form.EindDatum}
                      onChange={(e) => handleChange('EindDatum', e.target.value)} min={form.StartDatum || new Date().toISOString().split('T')[0]} />
                    {fieldErrors.EindDatum && <div style={styles.fieldError}>⚠ {fieldErrors.EindDatum}</div>}
                  </div>
                </div>
                {heleDagInfo && heleDagInfo.werkdagen > 0 && (
                  <div style={styles.field}>
                    <label style={styles.label}>Uren berekening</label>
                    <input type="text" inputMode="decimal" style={{ ...styles.input, ...(fieldErrors.Uren ? styles.inputError : {}) }} value={form.Uren}
                      onChange={(e) => handleChange('Uren', e.target.value)} min="1" step="0.5" />
                    <div style={styles.hintText}>Werkrooster: {heleDagInfo.werkdagen} werkdag{heleDagInfo.werkdagen > 1 ? 'en' : ''} = {heleDagInfo.uren} uur</div>
                    {fieldErrors.Uren && <div style={styles.fieldError}>⚠ {fieldErrors.Uren}</div>}
                  </div>
                )}
              </>
            ) : (
              <>
                <div style={styles.dateRow} className="r-form-row">
                  <div style={styles.field}>
                    <label style={styles.label}>Startdatum *</label>
                    <input type="date" style={{ ...styles.input, ...(fieldErrors.StartDatum ? styles.inputError : {}) }} value={form.StartDatum}
                      onChange={(e) => { handleChange('StartDatum', e.target.value); if (!form.EindDatum) handleChange('EindDatum', e.target.value); }}
                      min={new Date().toISOString().split('T')[0]} />
                    {fieldErrors.StartDatum && <div style={styles.fieldError}>⚠ {fieldErrors.StartDatum}</div>}
                  </div>
                  <div style={styles.field}>
                    <label style={styles.label}>Starttijd <span style={{ fontWeight: 400, color: '#a19f9d' }}>(optioneel)</span></label>
                    <input type="time" style={{ ...styles.input, ...(fieldErrors.StartTijd ? styles.inputError : {}) }} value={form.StartTijd}
                      onChange={(e) => handleChange('StartTijd', e.target.value)} />
                    {fieldErrors.StartTijd && <div style={styles.fieldError}>⚠ {fieldErrors.StartTijd}</div>}
                    {!form.StartTijd && <div style={styles.hintText}>Leeg = hele werkdag (werkrooster)</div>}
                  </div>
                </div>
                <div style={styles.dateRow} className="r-form-row">
                  <div style={styles.field}>
                    <label style={styles.label}>Einddatum</label>
                    <input type="date" style={{ ...styles.input, ...(fieldErrors.EindDatum ? styles.inputError : {}) }} value={form.EindDatum}
                      onChange={(e) => handleChange('EindDatum', e.target.value)}
                      min={form.StartDatum || new Date().toISOString().split('T')[0]} />
                    {fieldErrors.EindDatum && <div style={styles.fieldError}>⚠ {fieldErrors.EindDatum}</div>}
                  </div>
                  <div style={styles.field}>
                    <label style={styles.label}>Eindtijd <span style={{ fontWeight: 400, color: '#a19f9d' }}>(optioneel)</span></label>
                    <input type="time" style={{ ...styles.input, ...(fieldErrors.EindTijd ? styles.inputError : {}) }} value={form.EindTijd}
                      onChange={(e) => handleChange('EindTijd', e.target.value)} />
                    {fieldErrors.EindTijd && <div style={styles.fieldError}>⚠ {fieldErrors.EindTijd}</div>}
                    {!form.EindTijd && <div style={styles.hintText}>Leeg = hele werkdag (werkrooster)</div>}
                  </div>
                </div>

                {periodeInfo && periodeInfo.netto > 0 && (
                  <div style={styles.dagdeelPreview}>
                    <div style={styles.dagdeelUren}>
                      📅 {fmtDatum(form.StartDatum)} {form.StartTijd || '(hele dag)'} → {fmtDatum(form.EindDatum || form.StartDatum)} {form.EindTijd || '(hele dag)'}
                      {' = '}<strong>{periodeInfo.netto} uur</strong>
                      {periodeInfo.werkdagen > 1 && ` (${periodeInfo.werkdagen} werkdagen)`}
                    </div>
                    {periodeInfo.details.length > 1 && (
                      <div style={{ fontSize: 12, color: '#605e5c', marginTop: 6, lineHeight: 1.6 }}>
                        {periodeInfo.details.filter(d => d.uren > 0).map((d, i) => (
                          <div key={i}>
                            {fmtDatum(d.datum)}: {d.type === 'hele dag' ? `${d.uren}u (hele dag)` : `${d.van}–${d.tot} = ${d.uren}u`}
                            {d.pauzeTrekt && ' (−30min pauze)'}
                          </div>
                        ))}
                      </div>
                    )}
                    {periodeInfo.details.length === 1 && periodeInfo.details[0].pauzeTrekt && (
                      <div style={styles.pauzeNotice}>ℹ️ Pauze 12:30–13:00 wordt afgetrokken</div>
                    )}
                    {periodeInfo.details.length === 1 && !periodeInfo.details[0].pauzeTrekt && (
                      <div style={styles.geenPauzeNotice}>✓ Geen pauze-aftrek — tijdvak valt buiten de pauze</div>
                    )}
                  </div>
                )}

                {periodeInfo && periodeInfo.netto > 0 && (
                  <div style={styles.field}>
                    <label style={styles.label}>Uren berekening</label>
                    <input type="text" inputMode="decimal" style={{ ...styles.input, ...(fieldErrors.Uren ? styles.inputError : {}) }} value={form.Uren}
                      onChange={(e) => handleChange('Uren', e.target.value)} min="0.5" step="0.5" />
                    {fieldErrors.Uren && <div style={styles.fieldError}>⚠ {fieldErrors.Uren}</div>}
                  </div>
                )}
              </>
            )}

            {uren > 0 && (
              <div style={styles.previewBox}>
                📅 <strong>{uren}</strong> uur
                {form.isDagdeel && form.StartDatum && ` (${fmtDatum(form.StartDatum)} ${form.StartTijd || 'hele dag'} → ${fmtDatum(form.EindDatum || form.StartDatum)} ${form.EindTijd || 'hele dag'})`}
                {!form.isDagdeel && heleDagInfo && heleDagInfo.werkdagen > 0 && ` (${heleDagInfo.werkdagen} werkdag${heleDagInfo.werkdagen > 1 ? 'en' : ''})`}
              </div>
            )}

            {/* Overuren verrekening */}
            {uren > 0 && saldo && saldo.overurenSaldo > 0 && (() => {
              const selectedType = verlofTypes.find(t => t.key === form.Type);
              const isFlex = selectedType && selectedType.aftrekSaldo === 'flex';
              const isGeenAftrek = selectedType && (selectedType.aftrekSaldo === 'geen' || selectedType.trektAfVanVerlof === false);
              if (isFlex || isGeenAftrek) return null;
              const overurenBeschikbaar = saldo.overurenSaldo;
              const overurenGebruikt = form.gebruikOveruren ? Math.min(overurenBeschikbaar, uren) : 0;
              const restVerlof = Math.max(0, uren - overurenGebruikt);
              return (
                <div style={{ background: '#fff8f0', border: '1px solid #FFB900', borderRadius: 10, padding: '14px 18px', marginBottom: 20 }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', marginBottom: overurenGebruikt > 0 ? 10 : 0 }}>
                    <input type="checkbox" checked={form.gebruikOveruren} onChange={(e) => handleChange('gebruikOveruren', e.target.checked)} />
                    <span style={{ fontSize: 13, fontWeight: 600, color: '#7A6400' }}>⏰ Overuren inzetten ({overurenBeschikbaar}u beschikbaar)</span>
                  </label>
                  {overurenGebruikt > 0 && (
                    <div style={{ fontSize: 12, color: '#605e5c', lineHeight: 1.6 }}>Overuren: <strong>{overurenGebruikt}u</strong> · Verlof: <strong>{restVerlof}u</strong></div>
                  )}
                </div>
              );
            })()}

            {/* Saldo overzicht */}
            {saldo && !isVoorAnder && (
              <div style={{ background: '#faf9f8', border: '1px solid #edebe9', borderRadius: 10, padding: '14px 18px', marginBottom: 20, fontSize: 13, lineHeight: 1.8 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Verlofuren (contract):</span><strong>{saldo.contractUren}u</strong></div>
                {saldo.meegenomenUren > 0 && (<div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Meegenomen vorig jaar:</span><strong>+{saldo.meegenomenUren}u</strong></div>)}
                <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '1px solid #edebe9', paddingTop: 4, marginTop: 4 }}><span>Totaal beschikbaar:</span><strong>{saldo.totaalUren}u</strong></div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Opgenomen:</span><span>−{saldo.opgenomenUren}u</span></div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700, color: saldo.resterend >= 0 ? '#107C10' : '#D13438' }}><span>Resterend:</span><span>{saldo.resterend}u</span></div>
                {saldo.overurenSaldo > 0 && (<div style={{ display: 'flex', justifyContent: 'space-between', color: '#E86E1C', marginTop: 4 }}><span>⏰ Overuren saldo:</span><strong>{saldo.overurenSaldo}u</strong></div>)}
                {saldo.weekFlexUren > 0 && (
                  <>
                    <div style={{ display: 'flex', justifyContent: 'space-between', color: saldo.flexBalans >= 0 ? '#107C10' : '#D13438', marginTop: 4 }}><span>Flex/ADV balans (jaar):</span><strong>{saldo.flexBalans > 0 ? '+' : ''}{saldo.flexBalans}u</strong></div>
                    {isFlexType && (
                      <div style={{ marginTop: 6, padding: '8px 10px', background: '#F0F6FF', borderRadius: 8, border: '1px solid #c7e0f4', fontSize: 12 }}>
                        <div style={{ fontWeight: 700, marginBottom: 4, color: '#0078D4' }}>📊 Flex/ADV limieten</div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#323130' }}><span>Per week (flex-min):</span><span>max {saldo.flexMinWeekMax}u · nog <strong>{saldo.flexMinWeekBeschikbaar}u</strong></span></div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#323130' }}><span>Per maand (flex-min):</span><span>max {saldo.flexMinMaandMax}u · nog <strong>{saldo.flexMinMaandBeschikbaar}u</strong></span></div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: saldo.flexBalansDezeMaand >= 0 ? '#107C10' : '#D13438', marginTop: 4, borderTop: '1px solid #edebe9', paddingTop: 4 }}><span>Maandbalans (plus − min):</span><strong>{saldo.flexBalansDezeMaand > 0 ? '+' : ''}{saldo.flexBalansDezeMaand}u</strong></div>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            {/* Direct goedkeuren optie */}
            {isVoorAnder && uren > 0 && (
              <div style={{ background: form.directGoedkeuren ? '#F0FFF0' : '#faf9f8', border: `1px solid ${form.directGoedkeuren ? '#107C10' : '#edebe9'}`, borderRadius: 10, padding: '14px 18px', marginBottom: 20, transition: 'all .2s' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
                  <input type="checkbox" checked={form.directGoedkeuren} onChange={(e) => handleChange('directGoedkeuren', e.target.checked)} />
                  <span style={{ fontSize: 13, fontWeight: 600, color: form.directGoedkeuren ? '#107C10' : '#605e5c' }}>✅ Direct goedkeuren</span>
                </label>
                <div style={{ fontSize: 11, color: '#605e5c', marginTop: 4, marginLeft: 28 }}>
                  {form.directGoedkeuren ? 'Status wordt direct "goedgekeurd". De medewerker krijgt een melding.' : 'Status wordt "ingediend" — moet nog worden goedgekeurd.'}
                </div>
              </div>
            )}

            <div style={styles.field}>
              <label style={styles.label}>Opmerkingen (optioneel)</label>
              <textarea style={styles.textarea} value={form.Reden} onChange={(e) => handleChange('Reden', e.target.value)} placeholder="Bijv. familiebezoek..." rows={3} />
            </div>

            {error && <div style={styles.errorBox}>⚠️ {error}</div>}

            {(() => {
              if (!form.StartDatum || !vakantiePerioden || vakantiePerioden.length === 0) return null;
              const sd = form.StartDatum; const ed = form.EindDatum || form.StartDatum;
              const overlapping = vakantiePerioden.filter(vp => sd <= vp.eindDatum && ed >= vp.startDatum);
              if (overlapping.length === 0) return null;
              return (<div style={{ background: '#FFF4CE', border: '1px solid #FFB900', borderRadius: 10, padding: '12px 16px', fontSize: 13, color: '#7A6400', marginBottom: 12 }}>
                ⚠️ <strong>Let op:</strong> Je verlofaanvraag valt (deels) in een vakantieperiode ({overlapping.map(vp => vp.naam).join(', ')}).
              </div>);
            })()}

            <div style={styles.buttonRow}>
              <button style={styles.cancelButton} onClick={() => navigate('/')}>Annuleren</button>
              <button style={{ ...styles.submitButton, opacity: submitting ? 0.6 : 1, ...(isVoorAnder && form.directGoedkeuren ? { background: '#107C10' } : {}) }}
                onClick={handleSubmit} disabled={submitting}>
                {submitting ? 'Bezig...' : (isVoorAnder && form.directGoedkeuren ? '✅ Goedkeuren & indienen' : 'Aanvraag indienen')}
              </button>
            </div>
            </>
            )}
          </>
        )}
      </div>
    </div>
  );
}

const styles = {
  container: { maxWidth: 640, width: '100%', minWidth: 0 },
  formCard: { background: '#fff', borderRadius: 16, padding: 32, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' },
  formTitle: { fontSize: 20, fontWeight: 700, color: '#1a1a2e', marginBottom: 4 },
  formSubtitle: { fontSize: 14, color: '#605e5c', marginBottom: 28 },
  field: { marginBottom: 20, flex: 1, minWidth: 0 },
  label: { display: 'block', fontSize: 13, fontWeight: 600, color: '#323130', marginBottom: 6 },
  select: { width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #c8c6c4', fontSize: 14, color: '#323130', background: '#fff' },
  input: { width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #c8c6c4', fontSize: 14, color: '#323130' },
  textarea: { width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #c8c6c4', fontSize: 14, color: '#323130', resize: 'vertical', fontFamily: 'inherit' },
  dateRow: { display: 'flex', gap: 16 },
  hintText: { fontSize: 12, color: '#a19f9d', marginTop: 4 },
  fieldError: { fontSize: 11, color: '#a4262c', marginTop: 4, fontWeight: 500 },
  inputError: { borderColor: '#a4262c', boxShadow: '0 0 0 1px #a4262c' },
  toggleRow: { display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' },
  toggleBtn: { flex: '1 1 auto', padding: '10px 16px', borderRadius: 10, border: '2px solid #edebe9', background: '#faf9f8', fontSize: 13, fontWeight: 600, color: '#605e5c', cursor: 'pointer', transition: 'all .15s', minWidth: 0 },
  toggleBtnActive: { borderColor: '#E86E1C', background: '#FFF4EE', color: '#E86E1C' },
  dagdeelPreview: { background: '#f0f6ff', border: '1px solid #c7e0f4', borderRadius: 10, padding: '14px 18px', marginBottom: 20 },
  dagdeelUren: { fontSize: 15, color: '#0078D4', marginBottom: 4 },
  pauzeNotice: { fontSize: 12, color: '#8764B8', fontStyle: 'italic', lineHeight: 1.4 },
  geenPauzeNotice: { fontSize: 12, color: '#107C10', lineHeight: 1.4 },
  previewBox: { background: '#f0f6ff', border: '1px solid #c7e0f4', borderRadius: 8, padding: '12px 16px', fontSize: 14, color: '#0078D4', marginBottom: 20 },
  errorBox: { background: '#fde7e9', border: '1px solid #f1bbbc', borderRadius: 8, padding: '12px 16px', fontSize: 14, color: '#a4262c', marginBottom: 20 },
  buttonRow: { display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 8, flexWrap: 'wrap' },
  cancelButton: { padding: '10px 24px', borderRadius: 8, border: '1px solid #c8c6c4', background: '#fff', fontSize: 14, fontWeight: 600, color: '#605e5c', cursor: 'pointer' },
  submitButton: { padding: '10px 24px', borderRadius: 8, border: 'none', background: '#E86E1C', fontSize: 14, fontWeight: 600, color: '#fff', cursor: 'pointer' },
  successCard: { background: '#fff', borderRadius: 16, padding: 48, textAlign: 'center', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', maxWidth: 480 },
  successIcon: { fontSize: 48, marginBottom: 16 },
  successTitle: { fontSize: 22, fontWeight: 700, color: '#107C10', marginBottom: 8 },
  successText: { fontSize: 14, color: '#605e5c', lineHeight: 1.6 },
  wijzerTrigger: {
    display: 'block', width: '100%', padding: '10px 14px', marginTop: 8,
    borderRadius: 8, border: '1px dashed #8764B8', background: '#faf5ff',
    fontSize: 12, color: '#8764B8', cursor: 'pointer', textAlign: 'left',
    lineHeight: 1.5,
  },
  wijzerBadge: {
    marginTop: 8, padding: '10px 14px', borderRadius: 8,
    border: '2px solid', lineHeight: 1.5,
  },
  hubBtn: {
    display: 'flex', alignItems: 'center', gap: 14, padding: '16px 18px',
    borderRadius: 12, border: '2px solid #edebe9', background: '#fff',
    cursor: 'pointer', textAlign: 'left', transition: 'all .15s', width: '100%',
  },
  hubIcon: { fontSize: 28, flexShrink: 0 },
  hubLabel: { fontSize: 15, fontWeight: 700, color: '#1a1a2e' },
  hubDesc: { fontSize: 12, color: '#605e5c', marginTop: 2 },
  hubArrow: { marginLeft: 'auto', fontSize: 18, color: '#a19f9d', flexShrink: 0 },
};

export default LeaveRequest;
